import '@blocknote/core/style.css';
import '@blocknote/react/style.css';
import '@blocknote/ariakit/style.css';
import './block-editor.web.css';

import type { PartialBlock } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/ariakit';
import {
  AddBlockButton,
  DragHandleButton,
  SideMenu,
  SideMenuController,
  useCreateBlockNote,
} from '@blocknote/react';
import { Component, type ErrorInfo, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/ui';

import { fetchPageDocument, presignDownload, presignUpload, updateBlock } from '@/lib/blocks/api';
import { invalidatePageDocument } from '@/lib/blocks/page-cache';
import { useWebSyncPage } from '@/lib/sync/web-sync-context';

import { blocksToPartialBlocks } from './blocknote-types';
import {
  createCouplespaceMarkdownCopyExtension,
  createCouplespacePasteHandler,
} from './couplespace-markdown-clipboard.web';
import { CouplespaceDragHandleMenu } from './CouplespaceDragHandleMenu.web';
import { CouplespaceFormattingToolbarController } from './CouplespaceFormattingToolbar.web';
import { CouplespaceSlashMenuController } from './CouplespaceSlashMenu.web';
import { couplespaceTableEnterExtension } from './couplespace-table-enter.web';
import { couplespaceSchema } from './editor-schema.web';
import { DatabaseViewPickerHost } from './DatabaseViewPickerHost.web';
import { EditorDatabaseViewPickerProvider } from './editor-database-view-picker-context.web';
import { EditorLinkPickerProvider } from './editor-link-picker-context.web';
import { EditorPageProvider } from './editor-page-context.web';
import {
  blocksContentEqual,
  fixInlineLinkArtifacts,
  repairPartialBlocks,
  validateBlocksForPersist,
} from './sanitize-initial-content';
import { FILE_BLOCK_ID_PROP } from './file-block-persist';
import { buildSnapshot, syncDocument, type SyncSnapshot } from './sync-document.web';

type UploadFileResult = string | { props: Record<string, unknown> };

function isConflictError(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return false;
  }
  return (
    err.message.includes('CONFLICT') || err.message.includes('modified by another session')
  );
}

async function rebaseSnapshotFromServer(pageId: string): Promise<SyncSnapshot> {
  invalidatePageDocument(pageId);
  const doc = await fetchPageDocument(pageId);
  const versions = new Map<string, number>();
  for (const row of doc.contentBlocks) {
    versions.set(row.id, row.syncVersion);
  }
  const serverBlocks = blocksToPartialBlocks(doc.block.id, doc.contentBlocks);
  return buildSnapshot(pageId, serverBlocks, versions);
}

function collectImageBlockSummary(blocks: PartialBlock[]): string {
  const out: Array<{
    id: string | undefined;
    type: string;
    props: Record<string, unknown>;
    content: unknown;
  }> = [];
  function visit(block: PartialBlock): void {
    if (block.type === 'image' || block.type === 'file') {
      out.push({
        id: block.id,
        type: block.type,
        props: { ...((block.props as Record<string, unknown> | undefined) ?? {}) },
        content: block.content,
      });
    }
    block.children?.forEach(visit);
  }
  blocks.forEach(visit);
  return JSON.stringify(out);
}

function uploadFilename(file: File): string {
  const trimmed = file.name.trim();
  if (trimmed.length > 0) {
    return trimmed;
  }
  const subtype = file.type.split('/')[1];
  if (subtype) {
    return subtype === 'jpeg' ? 'upload.jpg' : `upload.${subtype}`;
  }
  return 'upload.bin';
}

type BlockEditorProps = {
  pageId: string;
  pageTitle: string;
  pageSyncVersion: number;
  onPageTitleChange?: (title: string) => void;
  initialContent: PartialBlock[];
  syncVersions: Map<string, number>;
  loading?: boolean;
  onRemoteReload?: () => void;
};

export function BlockEditor({
  pageId,
  pageTitle,
  pageSyncVersion,
  onPageTitleChange,
  initialContent,
  syncVersions,
  loading,
  onRemoteReload,
}: BlockEditorProps) {
  if (loading) {
    return (
      <View className="min-h-full flex-1 items-center justify-center">
        <ActivityIndicator size="small" />
      </View>
    );
  }

  return (
    <BlockEditorErrorBoundary>
      <BlockNoteEditorInner
        key={pageId}
        initialContent={initialContent}
        onPageTitleChange={onPageTitleChange}
        onRemoteReload={onRemoteReload}
        pageId={pageId}
        pageSyncVersion={pageSyncVersion}
        pageTitle={pageTitle}
        syncVersions={syncVersions}
      />
    </BlockEditorErrorBoundary>
  );
}

type BlockEditorErrorBoundaryProps = {
  children: ReactNode;
};

type BlockEditorErrorBoundaryState = {
  error: Error | null;
};

class BlockEditorErrorBoundary extends Component<
  BlockEditorErrorBoundaryProps,
  BlockEditorErrorBoundaryState
> {
  state: BlockEditorErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BlockEditorErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Block editor failed to render', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <View className="mx-auto my-6 w-full max-w-content rounded-lg border border-danger bg-danger-muted px-4 py-3">
          <Text variant="body-sm" className="font-medium text-danger">
            Editor failed to load
          </Text>
          <Text variant="body-sm" className="mt-1 text-fg-muted">
            This page has content the editor could not recover. Reload the page or reset the
            document below.
          </Text>
          <Pressable
            className="mt-3 self-start rounded-md bg-danger px-3 py-2"
            onPress={() => this.setState({ error: null })}
          >
            <Text variant="body-sm" className="text-fg-inverse">
              Try again
            </Text>
          </Pressable>
        </View>
      );
    }
    return this.props.children;
  }
}

type BlockNoteEditorInnerProps = {
  pageId: string;
  pageTitle: string;
  pageSyncVersion: number;
  onPageTitleChange?: (title: string) => void;
  initialContent: PartialBlock[];
  syncVersions: Map<string, number>;
  onRemoteReload?: () => void;
};

function BlockNoteEditorInner({
  pageId,
  pageTitle,
  pageSyncVersion,
  onPageTitleChange,
  initialContent,
  syncVersions,
  onRemoteReload,
}: BlockNoteEditorInnerProps) {
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { registerEditor, notifyLocalSave } = useWebSyncPage(pageId);
  const { blocks: safeInitialContent, repaired: contentWasRepaired } = useMemo(
    () => repairPartialBlocks(initialContent),
    [initialContent],
  );
  const snapshotRef = useRef<SyncSnapshot>(
    buildSnapshot(pageId, safeInitialContent, syncVersions),
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef(false);
  const savingRef = useRef(false);
  const saveQueuedRef = useRef(false);
  const repairPersistedRef = useRef(false);
  const uploadsInFlightRef = useRef(0);
  const repairingLinksRef = useRef(false);
  const editorShellRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const flushSaveRef = useRef<(forceBlocks?: PartialBlock[]) => Promise<void>>(() =>
    Promise.resolve(),
  );
  const pageTitleRef = useRef(pageTitle);
  const pageSyncVersionRef = useRef(pageSyncVersion);
  const editorRef = useRef<ReturnType<typeof useCreateBlockNote<{
    schema: typeof couplespaceSchema;
  }>> | null>(null);
  pageTitleRef.current = pageTitle;
  pageSyncVersionRef.current = pageSyncVersion;

  const markdownClipboardRefs = useMemo(
    () => ({
      getPageTitle: () => pageTitleRef.current,
      onPageTitleFromPaste: async (title: string) => {
        const trimmed = title.trim() || 'Untitled';
        const updated = await updateBlock(pageId, {
          title: trimmed,
          expectedSyncVersion: pageSyncVersionRef.current,
        });
        pageSyncVersionRef.current = updated.syncVersion;
        onPageTitleChange?.(trimmed);
      },
    }),
    [onPageTitleChange, pageId],
  );

  const markdownCopyExtension = useMemo(
    () =>
      createCouplespaceMarkdownCopyExtension(
        () => editorRef.current,
        markdownClipboardRefs,
      ),
    [markdownClipboardRefs],
  );

  const uploadFile = useMemo(
    () => async (file: File): Promise<UploadFileResult> => {
      uploadsInFlightRef.current += 1;
      notifyLocalSave();
      console.log('[CS-IMG] uploadFile:start', {
        name: file.name,
        type: file.type,
        size: file.size,
        inFlight: uploadsInFlightRef.current,
      });
      try {
        const { fileId, uploadUrl } = await presignUpload({
          blockId: pageId,
          filename: uploadFilename(file),
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        });
        console.log('[CS-IMG] presignUpload:ok', { fileId, uploadUrl });
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        console.log('[CS-IMG] put:done', { status: putRes.status, ok: putRes.ok });
        if (!putRes.ok) {
          throw new Error(`Upload failed (${putRes.status})`);
        }
        const url = await presignDownload(fileId);
        console.log('[CS-IMG] presignDownload:ok', { fileId, url });
        const result = {
          props: {
            [FILE_BLOCK_ID_PROP]: fileId,
            url,
            name: uploadFilename(file),
          },
        };
        console.log('[CS-IMG] uploadFile:return', result);
        return result;
      } catch (err) {
        console.error('[CS-IMG] uploadFile:error', err);
        throw err;
      } finally {
        uploadsInFlightRef.current -= 1;
        console.log('[CS-IMG] uploadFile:finally', {
          inFlight: uploadsInFlightRef.current,
        });
        globalThis.setTimeout(() => {
          console.log('[CS-IMG] post-upload flushSave trigger');
          void flushSaveRef.current();
        }, 0);
      }
    },
    [notifyLocalSave, pageId],
  );

  const editor = useCreateBlockNote({
    schema: couplespaceSchema,
    extensions: [couplespaceTableEnterExtension, markdownCopyExtension],
    initialContent: safeInitialContent.length > 0 ? safeInitialContent : undefined,
    pasteHandler: createCouplespacePasteHandler(markdownClipboardRefs),
    uploadFile,
  });
  editorRef.current = editor;

  useEffect(() => {
    if (!onRemoteReload) {
      return;
    }
    return registerEditor({
      isDirty: () =>
        pendingRef.current ||
        savingRef.current ||
        debounceRef.current !== null ||
        uploadsInFlightRef.current > 0,
      reload: onRemoteReload,
    });
  }, [onRemoteReload, registerEditor]);

  async function flushSave(forceBlocks?: PartialBlock[]): Promise<void> {
    if (forceBlocks === undefined && uploadsInFlightRef.current > 0) {
      console.log('[CS-IMG] flushSave:skip (upload in flight)', {
        inFlight: uploadsInFlightRef.current,
      });
      return;
    }
    if (pendingRef.current) {
      console.log('[CS-IMG] flushSave:queue (already saving)');
      saveQueuedRef.current = true;
      return;
    }
    const docSnapshot = forceBlocks ?? (editor.document as PartialBlock[]);
    console.log('[CS-IMG] flushSave:start', {
      forced: forceBlocks !== undefined,
      imageBlocks: collectImageBlockSummary(docSnapshot),
    });
    pendingRef.current = true;
    savingRef.current = true;
    setSaving(true);

    let blocksOverride = forceBlocks;
    let savedOk = false;

    try {
      do {
        saveQueuedRef.current = false;
        setSaveError(null);

        const source = blocksOverride ?? (editor.document as PartialBlock[]);
        blocksOverride = undefined;

        const blocks = validateBlocksForPersist(source);
        if (!blocks) {
          console.warn('[CS-IMG] flushSave:invalid content, abort');
          setSaveError('Unable to save: document contains invalid content');
          return;
        }
        console.log('[CS-IMG] flushSave:persist', {
          imageBlocks: collectImageBlockSummary(blocks),
        });

        try {
          snapshotRef.current = await syncDocument(pageId, blocks, snapshotRef.current);
          invalidatePageDocument(pageId);
          savedOk = true;
        } catch (err) {
          if (!isConflictError(err)) {
            throw err;
          }
          snapshotRef.current = await rebaseSnapshotFromServer(pageId);
          snapshotRef.current = await syncDocument(pageId, blocks, snapshotRef.current);
          invalidatePageDocument(pageId);
          savedOk = true;
        }
      } while (saveQueuedRef.current);
      console.log('[CS-IMG] flushSave:done', { savedOk });
    } catch (err) {
      console.error('[CS-IMG] flushSave:error', err);
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
      pendingRef.current = false;
      savingRef.current = false;
      if (savedOk) {
        notifyLocalSave();
      }
    }
  }

  useEffect(() => {
    if (!contentWasRepaired || repairPersistedRef.current) {
      return;
    }
    if (blocksContentEqual(safeInitialContent, initialContent)) {
      return;
    }
    repairPersistedRef.current = true;
    void flushSave(safeInitialContent);
  }, [contentWasRepaired, initialContent, safeInitialContent]);

  flushSaveRef.current = flushSave;

  function repairLinkArtifactsInEditor(): void {
    if (repairingLinksRef.current) {
      return;
    }
    repairingLinksRef.current = true;
    try {
      for (const block of editor.document as PartialBlock[]) {
        if (!block.id || !Array.isArray(block.content)) {
          continue;
        }
        const fixed = fixInlineLinkArtifacts(block.content);
        if (JSON.stringify(fixed) !== JSON.stringify(block.content)) {
          editor.updateBlock(block.id, { content: fixed });
        }
      }
    } finally {
      repairingLinksRef.current = false;
    }
  }

  function scheduleSave(): void {
    repairLinkArtifactsInEditor();
    console.log('[CS-IMG] onChange/scheduleSave', {
      inFlight: uploadsInFlightRef.current,
      imageBlocks: collectImageBlockSummary(editor.document as PartialBlock[]),
    });
    // While an upload is in flight, any onChange comes from BlockNote inserting
    // a placeholder block or applying intermediate updates. The post-upload
    // `setTimeout(0)` in `uploadFile`'s finally block is what triggers the real
    // save once the resolved url has been applied to the block. Clear any prior
    // debounce so a timer set before the upload started cannot fire mid-upload.
    if (uploadsInFlightRef.current > 0) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      return;
    }
    const blocks = validateBlocksForPersist(editor.document as PartialBlock[]);
    if (!blocks) {
      setSaveError('Unable to save: document contains invalid content');
      return;
    }
    setSaveError(null);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void flushSave();
    }, 500);
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const shell = editorShellRef.current;
    if (!shell) {
      return;
    }
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest('a');
      if (!anchor) {
        return;
      }
      const href = anchor.getAttribute('href');
      if (!href || (!href.startsWith('/page/') && !href.startsWith('/database/'))) {
        return;
      }
      event.preventDefault();
      router.push(href);
    };
    shell.addEventListener('click', onClick);
    return () => {
      shell.removeEventListener('click', onClick);
    };
  }, [router]);

  return (
    <View className="mx-auto w-full max-w-content px-page pb-16 pt-2">
      {contentWasRepaired ? (
        <View className="mb-3 rounded-lg border border-warning bg-warning-muted px-3 py-2">
          <Text variant="body-sm" className="text-fg">
            Some content on this page was repaired after corruption. The fixed version is being
            saved.
          </Text>
        </View>
      ) : null}
      <div ref={editorShellRef} className="couplespace-editor min-h-[240px] w-full">
        <EditorPageProvider pageId={pageId}>
          <EditorLinkPickerProvider>
            <EditorDatabaseViewPickerProvider>
              <BlockNoteView
                editor={editor}
                onChange={scheduleSave}
                formattingToolbar={false}
                slashMenu={false}
                sideMenu={false}
                theme="light"
              >
                <CouplespaceSlashMenuController />
                <DatabaseViewPickerHost />
                <CouplespaceFormattingToolbarController />
              <SideMenuController
                sideMenu={(props) => (
                  <SideMenu {...props}>
                    <AddBlockButton />
                    <DragHandleButton dragHandleMenu={CouplespaceDragHandleMenu} />
                  </SideMenu>
                )}
              />
              </BlockNoteView>
            </EditorDatabaseViewPickerProvider>
          </EditorLinkPickerProvider>
        </EditorPageProvider>
      </div>
      <View className="mt-2 h-5">
        {saving ? <Text className="text-xs text-muted">Saving…</Text> : null}
        {saveError ? <Text variant="caption" className="text-danger">{saveError}</Text> : null}
      </View>
    </View>
  );
}
